/**
 * @fileoverview Files — Compass, at `/workspace/compass`.
 *
 * THE LAYOUT IS foundry-ui's; THE MAPPING IS OURS.
 *
 * This was ~900 lines: a facet rail, a breadcrumb bar, a four-column case table
 * and the state to drive them. It used Compass's TABLE components inside a
 * layout of our own, which is exactly what made it look like the Compass app
 * without being it — and comparing against `/workspace/compass` on the tenant,
 * the entire top of the page was absent: the All files / Shared with you / Data
 * Catalog / Trash tabs, the breadcrumb with its space selector and green New,
 * the Quick-filters strip, the full-width search, and a filter rail with
 * per-type counts.
 *
 * foundry-ui ships `compass-container`: hand-refined against the same capture,
 * and its header records a mistake I had already started making — the
 * quick-filters strip has ONE "Hide" and a per-card "Apply", "NOT a dismissible
 * card each, which is what an earlier draft of this file invented".
 *
 * It takes a `BrowserGateway`, not data: the shared BROWSER archetype port that
 * `layout-files-folder` implements too. "The two screens look nothing alike and
 * take the same data, which only works because rows are keyed by COLUMN."
 *
 * So this file is now the wiring, and everything specific to cases — which
 * columns, which facets, what the quick filters mean — lives in
 * `hex/adapters/case-browser-gateway.ts`.
 *
 * WHAT IS LOST, SAID PLAINLY. The old page drilled into a case to list its
 * evidence, and the block has no drill-in: rows carry an `href`, so a click
 * navigates instead. Evidence-in-a-case needs its own surface, and pretending
 * otherwise by keeping the old two-level explorer would mean keeping the layout
 * this replaces.
 */

import { useMemo, type ReactElement } from "react";
import { useNavigate } from "react-router";

import { CompassContainer } from "../components/compass/browser/compass-container";
import { Module, ModuleHeader } from "../components/module";
// `PanelProps` is Blueprint's — the panel-stack contract — not ours. Its
// namesake in components/panels/types is a different thing entirely.
import type { PanelProps } from "@blueprintjs/core";
import type { FilesPanelProps } from "../components/panels/types";
import { makeCaseBrowserGateway } from "../hex/adapters/case-browser-gateway";
import type { Case } from "../lib/api";
import { useCases } from "../lib/hooks";

export function Files(_props: PanelProps<FilesPanelProps>): ReactElement {
  const navigate = useNavigate();
  const casesQuery = useCases();
  const cases = useMemo<Case[]>(() => casesQuery.data ?? [], [casesQuery.data]);

  /*
    Memoised on `cases`: the block calls `load()` from an effect keyed by the
    gateway, so a fresh object every render would re-fetch forever.
  */
  const gateway = useMemo(() => makeCaseBrowserGateway(cases), [cases]);

  return (
    <Module data-testid="files" header={<ModuleHeader title="Files" />}>
      {/*
        `.bp-compass` is the element Foundry's Blueprint TOKENS are scoped to in
        the vendored stylesheet — styles/vendor/foundry-compass.generated.css.
      */}
      <div className="bp-compass">
        <CompassContainer gateway={gateway} onCreate={() => navigate("/workspace/compass?new=1")} />
      </div>
    </Module>
  );
}
